import { identifyFee } from '@vp/om-tax-display';
import cartActionTypes from '../cart/actionTypes';
import pricingActionTypes from './actionTypes';
import cartItemActionTypes from '../cartItems/actionTypes';
import createReducer from '../utils/createReducer';
import { applyLineItemMerchAttrNames } from './pricingUtils';
import pricingSelectors from './selectors';

//
// Reducers for 'state.pricing' portion of Redux state.
//
const initialState = {
  pricingIsValid: false,
  currency: '?',
  grossListCents: 0,
  netListCents: 0,
  grossDiscountedCents: 0,
  netDiscountedCents: 0,
  taxTotalCents: 0,
  itemPricing: {},
  itemGroupPricing: {},
  templatePricing: {},
  vatId: '',
  discountSource: null,
  couponErrorMessageKey: null,
  couponChangeInProgress: false,
  shippingPricing: {},
  previouslyAppliedDiscountInfo: undefined,
  shippingPricingData: undefined,
};

export default createReducer(initialState)({
  [cartItemActionTypes.CARTITEMS_REMOVE_START]: state => ({
    ...state,
    pricingIsValid: false,
    shippingPricing: { ...state.shippingPricing },
  }),
  [cartItemActionTypes.CARTITEMS_REMOVE_FAIL]: state => ({
    ...state,
    pricingIsValid: true,
  }),
  [cartItemActionTypes.CARTITEMS_REMOVE_SUCCESS]: (state, { itemId }) => ({
    ...state,
    pricingIsValid: false,
    shippingPricing: { ...state.shippingPricing },
    itemPricing: {
      ...state.itemPricing,
      [itemId]: undefined,
    },
  }),
  [cartItemActionTypes.CARTITEMGROUP_REMOVE_START]: state => ({
    ...state,
    pricingIsValid: false,
    shippingPricing: { ...state.shippingPricing },
  }),
  [cartItemActionTypes.CARTITEMGROUP_REMOVE_FAIL]: state => ({
    ...state,
    pricingIsValid: true,
  }),
  [cartItemActionTypes.CARTITEMGROUP_REMOVE_SUCCESS]: (state, { itemIds }) => {
    const { itemPricing } = state;
    itemIds.forEach(itemId => (itemPricing[itemId] = undefined));

    return {
      ...state,
      pricingIsValid: false,
      shippingPricing: { ...state.shippingPricing },
      itemPricing,
    };
  },
  [cartItemActionTypes.CARTITEMS_QUANTITY_UPDATE_START]: state => ({
    ...state,
    pricingIsValid: false,
    shippingPricing: { ...state.shippingPricing },
  }),
  [cartItemActionTypes.CARTITEMS_QUANTITY_UPDATE_FAIL]: state => ({
    ...state,
    pricingIsValid: true,
  }),
  [cartItemActionTypes.CARTITEMS_QUANTITY_UPDATE_SUCCESS]: state => ({
    ...state,
    pricingIsValid: false,
    shippingPricing: { ...state.shippingPricing },
  }),
  [cartActionTypes.CART_LOAD_SUCCESS]: (state, action) => {
    const {
      cart: { pricingData, lineItems, lineItemGroupData, shippingData },
    } = action;
    const itemPricing = {};
    const templatePricing = {};
    lineItems.forEach(item => {
      const itemPricingData = applyLineItemMerchAttrNames(item);
      itemPricing[item.id] = {
        ...itemPricingData,
        groupId: item.groupId,
        shippingPricingData: item.shippingData.pricingData
          ? {
              ...item.shippingData.pricingData,
              components: item.shippingData.pricingData.components?.map(
                component => ({
                  ...component,
                  // tag the shipping components with their fee identifier OR 'shipping' if not a fee
                  identifiers: [identifyFee(component) ?? 'shipping'],
                })
              ),
            }
          : undefined,
      };

      if (
        item.cartTemplateReference &&
        item.customOrderData?.payment?.templateBillingDisplayType
      ) {
        const templatePricingData = item.cartTemplateReference
          .cartTemplateSummary
          ? item.cartTemplateReference.cartTemplateSummary.pricingData
          : {};

        const itemTemplatePricing = {
          ...templatePricingData,
          itemId: item.id,
          cartTemplateId: item.cartTemplateReference.cartTemplateId,
          tenantName: item.cartTemplateReference.tenantName,
          templateBillingDisplayType:
            item.customOrderData.payment.templateBillingDisplayType,
        };

        templatePricing[item.id] = itemTemplatePricing;
      }
    });

    const itemGroupPricing = {};
    if (lineItemGroupData) {
      lineItemGroupData.forEach(group => {
        itemGroupPricing[group.groupId] = group.pricingData;
      });
    }

    let previouslyAppliedDiscountInfo;
    // if this action cart reload was triggered by a coupon entry, then store the
    //  "previous" state of the cart discount
    if (state.couponChangeInProgress) {
      previouslyAppliedDiscountInfo = {
        grossDiscountedCents: state.grossDiscountedCents,
        couponCode: pricingSelectors.selectDiscountCodeName(state),
      };
    }

    return {
      ...state,
      ...pricingData,
      pricingIsValid: true,
      itemPricing,
      itemGroupPricing,
      templatePricing,
      shippingPricing: {
        pricingData:
          shippingData &&
          shippingData?.containsDeliverableProducts &&
          shippingData?.shippingPricingStatus === 'Success'
            ? shippingData.pricingData
            : undefined,
        containsDeliverableProducts:
          !!shippingData?.containsDeliverableProducts,
        shippingMethodName: shippingData?.shippingMethodName,
        localizedDisplayInfo: shippingData?.localizedDisplayInfo,
      },
      couponChangeInProgress: false,
      previouslyAppliedDiscountInfo,
    };
  },
  [cartActionTypes.CART_CLEAR]: () => initialState,
  [pricingActionTypes.CLEAR_COUPON_START]: (state, action) => ({
    ...state,
    pricingIsValid: false,
    shippingPricing: { ...state.shippingPricing },
    couponErrorMessageKey: undefined,
    couponChangeInProgress: true,
  }),
  [pricingActionTypes.SET_COUPON_START]: (state, action) => ({
    ...state,
    pricingIsValid: false,
    shippingPricing: { ...state.shippingPricing },
    couponErrorMessageKey: undefined,
    couponChangeInProgress: true,
  }),
  [pricingActionTypes.CLEAR_COUPON_FAIL]: (state, action) => ({
    ...state,
    pricingIsValid: true,
    couponChangeInProgress: false,
    couponErrorMessageKey: action.error.couponErrorMessageKey,
  }),
  [pricingActionTypes.SET_COUPON_FAIL]: (state, action) => ({
    ...state,
    pricingIsValid: true,
    couponChangeInProgress: false,
    couponErrorMessageKey: action.error.couponErrorMessageKey,
    couponErrorSubstitutions: action.error.couponErrorSubstitutions,
  }),
  [pricingActionTypes.CLEAR_COUPON_SUCCESS]: (state, action) => ({
    ...state,
    discountSource: undefined,
    couponErrorMessageKey: undefined,
    couponChangeInProgress: false,
  }),
  [pricingActionTypes.SET_COUPON_SUCCESS]: (state, action) => ({
    ...state,
    couponErrorMessageKey: undefined,
  }),
});
